// ClassificationStep component - matches product-merchant-crud.html Step 2 exactly
// Simple categorization, brand selection, qualification, and product specifications
// Refactored: State and logic extracted to useClassificationStep hook
// Sub-components: LicenseDocumentSection, ProductSpecificationSection

import React from "react";
import { WizardStepComponentProps } from "src/shared/forms/types/WizardTypes";
import { FormField } from "src/shared/forms/components";
import { CategoryBrowser } from "src/shared/forms/components/CategoryBrowser";
import type { CategoryItem } from "src/shared/forms/components/CategoryBrowser/CategoryBrowser";
import { useCategoryName } from "src/app/modules/category/hooks/useCategoryName";

// Import extracted utilities and hooks
import { useClassificationStep } from "../../utils/useClassificationStep";
import { AVAILABLE_BRANDS } from "../../utils/classificationConstants";
import {
  classNames,
  getCategoryPlaceholder,
  getFieldError,
} from "../../utils/classificationHelpers";

// Import sub-components
import { LicenseDocumentSection, ProductSpecificationSection } from "./components";

// Interface enhanced to support dynamic categories and attributes
export interface ClassificationData {
  // Categorization & Classification
  categoryId?: string; // Selected category ID (leaf category)
  categoryPath?: CategoryItem[]; // Full hierarchical path
  brandId?: string;
  brandName?: string;

  // Dynamic Product Specification Attributes
  categoryAttributes?: { [attributeName: string]: any };

  // Qualification
  licenseNumber?: string;
  licenseFile?: File | null;

  // File upload integration fields
  licenseFileId?: string; // Server file ID from upload
  licenseFileUrl?: string; // Server file URL
  licenseUploadStatus?: 'idle' | 'uploading' | 'success' | 'error';
  licenseUploadError?: string;
}

export interface ClassificationStepProps extends WizardStepComponentProps {
  data: ClassificationData;
  onChange: (data: ClassificationData) => void;
  errors?: string[];
  isActive?: boolean;
  onValidate?: () => string[];
}

const ClassificationStep: React.FC<ClassificationStepProps> = ({
  data: classificationData,
  onChange,
  errors = [],
}) => {
  // Use custom hook for all state and logic
  const {
    showBrandDropdown,
    setShowBrandDropdown,
    brandSearchQuery,
    setBrandSearchQuery,
    showCategoryModal,
    setShowCategoryModal,
    rootCategories,
    rootLoading,
    rootError,
    leafCategoryId,
    filteredBrands,
    attributesLoading,
    attributesError,
    getSortedAttributes,
    handleFieldChange,
    handleBrandSelect,
    loadChildren,
    handleCategorySelection,
    handleLicenseFileChange,
    removeLicenseFile,
    retryLicenseUpload,
    openCategoryModal,
    updateCategoryAttributes,
  } = useClassificationStep({ classificationData, onChange });

  // Get language-aware category name helper
  const { getCategoryPathName } = useCategoryName();

  return (
    <div className="tw-max-w-full sm:tw-max-w-3xl lg:tw-max-w-4xl tw-mx-auto tw-space-y-4 md:tw-space-y-6">
      {/* Categorization & Classification Group */}
      <div className="tw-bg-surface-light dark:tw-bg-surface-dark tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Categorization & Classification
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Select category and brand to help customers find your product.
          </p>
        </div>

        <div className="tw-grid tw-gap-6">
          {/* Category Selection */}
          <FormField
            label="Category"
            name="category"
            required
            error={getFieldError(errors, "category")}
          >
            <div className="tw-relative">
              <button
                type="button"
                onClick={openCategoryModal}
                disabled={rootLoading || !!rootError}
                className={classNames(
                  "tw-w-full tw-bg-white dark:tw-bg-transparent",
                  "tw-border tw-border-gray-300 dark:tw-border-[#364152]",
                  "tw-rounded-lg tw-px-4 tw-py-2.5",
                  "tw-text-left tw-flex tw-justify-between tw-items-center",
                  "tw-transition-all tw-group",
                  "focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent",
                  rootLoading || !!rootError
                    ? "tw-opacity-50 tw-cursor-not-allowed"
                    : "hover:tw-border-gray-400 dark:hover:tw-border-gray-500"
                )}
              >
                <span
                  className={classNames(
                    classificationData.categoryPath && classificationData.categoryPath.length > 0
                      ? "tw-text-gray-900 dark:tw-text-white tw-font-medium"
                      : "tw-text-gray-500 dark:tw-text-gray-400 group-hover:tw-text-gray-700 dark:group-hover:tw-text-gray-300"
                  )}
                >
                  {rootLoading ? (
                    <span className="tw-flex tw-items-center tw-gap-2">
                      <div className="tw-animate-spin tw-h-4 tw-w-4 tw-border-2 tw-border-primary tw-border-t-transparent tw-rounded-full"></div>
                      Loading categories...
                    </span>
                  ) : (
                    getCategoryPathName(classificationData.categoryPath || []) ||
                    getCategoryPlaceholder(rootLoading, rootError)
                  )}
                </span>
                <span className="material-symbols-outlined tw-text-gray-400 group-hover:tw-text-gray-600">
                  view_column
                </span>
              </button>
            </div>
          </FormField>

          {/* Brand Selection */}
          <FormField
            label="Brand"
            name="brand"
            required
            error={getFieldError(errors, "brand")}
          >
            <div className="tw-relative">
              <button
                type="button"
                onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                className="tw-w-full tw-bg-white dark:tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-left tw-flex tw-justify-between tw-items-center focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all tw-group hover:tw-border-gray-400 dark:hover:tw-border-gray-500"
              >
                <span
                  className={classNames(
                    classificationData.brandName
                      ? "tw-text-gray-900 dark:tw-text-white tw-font-medium"
                      : "tw-text-gray-500 dark:tw-text-gray-400 group-hover:tw-text-gray-700 dark:group-hover:tw-text-gray-300"
                  )}
                >
                  {classificationData.brandName || "Select a brand..."}
                </span>
                <span
                  className={classNames(
                    "material-symbols-outlined tw-text-gray-400 group-hover:tw-text-gray-600 tw-transition-transform",
                    showBrandDropdown ? "tw-rotate-180" : ""
                  )}
                >
                  expand_more
                </span>
              </button>

              {/* Brand Dropdown */}
              {showBrandDropdown && (
                <div className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-bg-white dark:tw-bg-[#1C252E] tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-shadow-lg tw-z-20 tw-max-h-60 tw-overflow-y-auto">
                  <div className="tw-p-3 tw-border-b tw-border-gray-200 dark:tw-border-[#364152]">
                    <input
                      type="text"
                      placeholder="Search brands..."
                      value={brandSearchQuery}
                      onChange={(e) => setBrandSearchQuery(e.target.value)}
                      className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary tw-bg-transparent tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500"
                    />
                  </div>
                  <div className="tw-py-1">
                    {filteredBrands.map((brand) => (
                      <button
                        key={brand.id}
                        type="button"
                        onClick={() => handleBrandSelect(brand)}
                        className={classNames(
                          "tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648] tw-flex tw-items-center tw-gap-3",
                          classificationData.brandId === brand.id
                            ? "tw-bg-primary/5 dark:tw-bg-primary/10 tw-text-primary"
                            : "tw-text-gray-900 dark:tw-text-white"
                        )}
                      >
                        <span>{brand.name}</span>
                      </button>
                    ))}
                    {filteredBrands.length === 0 && (
                      <div className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                        No brands found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </FormField>
        </div>
      </div>

      {/* Qualification Group - Extracted Component */}
      <LicenseDocumentSection
        licenseNumber={classificationData.licenseNumber}
        licenseFile={classificationData.licenseFile}
        licenseUploadStatus={classificationData.licenseUploadStatus}
        licenseUploadError={classificationData.licenseUploadError}
        onLicenseNumberChange={(value) => handleFieldChange("licenseNumber", value)}
        onFileChange={handleLicenseFileChange}
        onFileRemove={removeLicenseFile}
        onRetryUpload={retryLicenseUpload}
      />

      {/* CategoryBrowser Modal */}
      <CategoryBrowser
        rootCategories={rootCategories}
        rootLoading={rootLoading}
        loadChildren={loadChildren}
        selectedCategory={classificationData.categoryPath || []}
        onChange={handleCategorySelection}
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
      />

      {/* Category Loading Errors */}
      {rootError && (
        <div className="tw-mt-2 tw-text-sm tw-text-red-600">
          Error loading categories: {rootError}
        </div>
      )}
      {rootCategories.length === 0 && !rootLoading && !rootError && (
        <p className="tw-text-xs tw-text-orange-600 tw-mt-1">
          No categories found. Please contact support.
        </p>
      )}

      {/* Dynamic Product Specification - Extracted Component */}
      {leafCategoryId && (
        <ProductSpecificationSection
          attributesLoading={attributesLoading}
          attributesError={attributesError}
          sortedAttributes={getSortedAttributes()}
          categoryAttributeValues={classificationData.categoryAttributes || {}}
          onAttributeChange={(name, value) => updateCategoryAttributes({ [name]: value })}
        />
      )}

      {/* Step Errors Display */}
      {errors.length > 0 && (
        <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-rounded-lg tw-p-4 tw-mt-6">
          <div className="tw-flex tw-items-start tw-gap-3">
            <span className="material-symbols-outlined tw-text-red-600 dark:tw-text-red-400 tw-mt-0.5">
              error
            </span>
            <div>
              <h4 className="tw-font-medium tw-text-red-800 dark:tw-text-red-300 tw-mb-2">
                Please correct the following errors:
              </h4>
              <ul className="tw-text-sm tw-text-red-700 dark:tw-text-red-400 tw-space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassificationStep;
